import React from "react";
import { motion } from "framer-motion";
import { Heart, Star, Lock, BookOpen, Baby, Utensils, BookMarked } from "lucide-react";
import CheckoutButton from "./CheckoutButton";

const BADGES = [
  { icon: BookOpen, head: "150+", sub: "Recetas deliciosas" },
  { icon: Baby, head: "0–15", sub: "Años de edad" },
  { icon: Utensils, head: "Menús", sub: "Por edad" },
  { icon: BookMarked, head: "Guía", sub: "Completa" },
];

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#F4EBDE]"
    >
      <div className="absolute inset-0 grain pointer-events-none opacity-60" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-20 md:pb-28">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-start lg:items-center">
          {/* Left vertical badges */}
          <motion.aside
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="hidden md:flex md:col-span-2 flex-col items-center gap-6 lg:gap-7 pt-6"
          >
            {BADGES.map((b, i) => (
              <motion.div
                key={b.head}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.12, duration: 0.6 }}
                whileHover={{ y: -3 }}
                data-testid={`hero-badge-${i}`}
                className="flex flex-col items-center text-center"
              >
                <div className="w-[64px] h-[64px] lg:w-[72px] lg:h-[72px] rounded-full bg-[#E5E9DB] flex items-center justify-center shadow-[0_6px_18px_rgba(132,153,131,0.16)]">
                  <b.icon className="h-[18px] w-[18px] text-[#6F856E]" strokeWidth={1.6} />
                </div>
                <div className="mt-2.5 font-serif-display text-base lg:text-lg text-[#2A302A] leading-none">
                  {b.head}
                </div>
                <div className="text-[9px] tracking-[0.18em] uppercase text-[#8A948A] font-semibold mt-1.5 leading-tight max-w-[90px]">
                  {b.sub}
                </div>
              </motion.div>
            ))}
          </motion.aside>

          {/* Center text */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 md:col-span-5 lg:col-span-4 pt-4"
          >
            <div className="inline-flex items-center gap-2">
              <Heart className="h-3.5 w-3.5 text-[#C8A892] fill-[#C8A892]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#576057]">
                La guía favorita de miles de mamás
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-serif-display mt-6 text-[44px] sm:text-[56px] md:text-[60px] lg:text-[72px] leading-[1.02] tracking-tight text-[#2A302A] font-medium"
            >
              Se acabó el estrés de pensar{" "}
              <em className="italic text-[#849983] font-normal">qué cocinar</em> todos los días
            </h1>

            <p className="mt-7 max-w-md text-base md:text-[17px] leading-relaxed text-[#576057]">
              Más de <strong className="text-[#2A302A]">150 recetas saludables</strong>{" "}
              y organizadas por edad para alimentar a tus hijos con amor, practicidad
              y tranquilidad. Desde los{" "}
              <strong className="text-[#2A302A]">0 hasta los 15 años</strong>.
            </p>

            <div className="mt-7 flex items-center gap-3">
              <div className="flex items-center gap-1 text-[#D4A574]">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="h-4 w-4 fill-current" strokeWidth={0} />
                ))}
              </div>
              <span className="text-sm text-[#576057]">
                Aprobado por más de <strong className="text-[#2A302A]">+2,400 mamás</strong>
              </span>
            </div>

            {/* Mobile-only: product mockup placed between social proof and price for higher perceived value */}
            <motion.div
              className="md:hidden mt-8 -mx-2"
              animate={{ y: [-4, 4, -4] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            >
              <img loading="eager" decoding="async" fetchPriority="high"
                src="/img/mockup-hero.webp"
                alt="Ebook Pequeños Platos con planificador semanal, lista de compras, guía de porciones y 30 snacks saludables"
                className="w-full h-auto drop-shadow-[0_24px_50px_rgba(78,60,40,0.22)]"
              />
            </motion.div>

            {/* Mobile-only: key benefits badges between mockup and price */}
            <div
              data-testid="hero-mobile-badges"
              className="md:hidden mt-8 grid grid-cols-2 gap-3"
            >
              {BADGES.map((b, i) => (
                <div
                  key={b.head}
                  data-testid={`hero-badge-mobile-${i}`}
                  className="bg-white/70 backdrop-blur rounded-2xl p-3.5 border border-[#E8DDC9] flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-[#E5E9DB] flex items-center justify-center shrink-0">
                    <b.icon className="h-4 w-4 text-[#6F856E]" strokeWidth={1.6} />
                  </div>
                  <div className="leading-tight">
                    <div className="font-serif-display text-base text-[#2A302A]">
                      {b.head}
                    </div>
                    <div className="text-[9px] tracking-[0.18em] uppercase text-[#8A948A] font-semibold mt-0.5">
                      {b.sub}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 md:mt-7 inline-flex items-center gap-1.5 bg-[#C8A892]/20 border border-[#C8A892]/40 text-[#849983] text-[11px] tracking-[0.22em] uppercase font-semibold px-3 py-1.5 rounded-full">
              <span aria-hidden="true">🔥</span>
              <span>Oferta de lanzamiento</span>
            </div>

            <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-serif-display text-3xl md:text-4xl text-[#2A302A] leading-none whitespace-nowrap">
                USD 9.90
              </span>
              <span className="text-[#8A948A] line-through text-sm whitespace-nowrap">
                USD 87
              </span>
            </div>

            <div className="mt-5 flex flex-col gap-3 items-start">
              <CheckoutButton
                testId="hero-cta-button"
                size="xxl"
                label="SÍ, QUIERO ALIMENTAR MEJOR A MI HIJO"
                fullWidthMobile
              />
              <div className="flex items-center gap-1.5 text-[12px] text-[#576057]">
                <Lock className="h-3 w-3" />
                <span>Pago seguro · Acceso inmediato · Móvil, tablet y PC</span>
              </div>
            </div>
          </motion.div>

          {/* Right image (desktop only — mobile renders mockup inside center column) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="hidden md:block md:col-span-5 lg:col-span-6 relative overflow-visible"
          >
            <motion.img
              src="/img/mockup-hero.webp"
              alt="Ebook Pequeños Platos con planificador semanal, lista de compras, guía de porciones y 30 snacks saludables"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              animate={{ y: [-6, 6, -6] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="w-full lg:w-[118%] lg:max-w-none lg:-mr-[8%] xl:w-[122%] xl:-mr-[10%] h-auto drop-shadow-[0_40px_80px_rgba(78,60,40,0.28)]"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
